import { jsx, jsxs } from "react/jsx-runtime";
import { compose } from "recompose";
import React from "react";
import withEditorInteractions from "../withEditorInteractions";
import { ReactSelectField, tgFormValues } from "@teselagen/ui";
import { reduxForm } from "redux-form";
import { flatMap, forEach, keyBy } from "lodash-es";
import SimpleCircularOrLinearView from "../SimpleCircularOrLinearView";
import {
  getReverseComplementSequenceString,
  getSequenceDataBetweenRange,
  tidyUpSequenceData,
  shiftAnnotationsByLen
} from "@teselagen/sequence-utils";
import { getRangeLength, getSequenceWithinRange } from "@teselagen/range-utils";
function PCRTool(props) {
  const {
    sequenceData,
    dimensions: { width, height },
    forwardPrimer,
    reversePrimer,
    primerClicked
  } = props;
  const origSeqLen = sequenceData.sequence.length;
  forEach(sequenceData.primers, (p) => p.originalId = p.id);
  const primersById = keyBy(sequenceData.primers, "id");
  const fPrimer = primersById[forwardPrimer];
  const rPrimer = primersById[reversePrimer];
  let seqBetween;
  if (fPrimer && rPrimer) {
    const r = {
      start: fPrimer.start,
      end: rPrimer.end
    };
    const newSeqData = getSequenceDataBetweenRange(sequenceData, r);
    if (rPrimer.bases) {
      const rPrimerLen = getRangeLength(rPrimer, origSeqLen);
      newSeqData.sequence = getSequenceWithinRange(
        { start: 0, end: newSeqData.sequence.length - rPrimerLen - 1 },
        newSeqData.sequence
      );
      newSeqData.sequence = newSeqData.sequence + getReverseComplementSequenceString(rPrimer.bases);
      const newRPrimer = keyBy(newSeqData.primers, "id")[reversePrimer];
      newRPrimer.end = newSeqData.sequence.length - 1;
    }
    if (fPrimer.bases) {
      const fPrimerLength = getRangeLength(fPrimer, origSeqLen);
      const newSeqLen1 = newSeqData.sequence.length;
      newSeqData.sequence = getSequenceWithinRange(
        { start: fPrimerLength, end: newSeqData.sequence.length },
        newSeqData.sequence
      );
      newSeqData.sequence = fPrimer.bases + newSeqData.sequence;
      const newSeqLen2 = newSeqData.sequence.length;
      const diff = newSeqLen2 - newSeqLen1;
      shiftAnnotationsByLen({
        seqData: newSeqData,
        insertLength: diff,
        caretPosition: 0
      });
      const newFPrimer = keyBy(newSeqData.primers, "id")[forwardPrimer];
      newFPrimer.start = 0;
    }
    seqBetween = tidyUpSequenceData(newSeqData, {
      provideNewIdsForAnnotations: true
    });
    seqBetween.name = `PCR Product from ${sequenceData.name} `;
  }
  const getPrimers = (opts) => {
    return flatMap(
      sequenceData.primers,
      ({ name, id, forward, start, end }) => {
        if (opts.forward && !forward) return [];
        if (!opts.forward && forward) return [];
        return {
          label: /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              },
              children: [
                /* @__PURE__ */ jsx("div", { children: name }),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      marginLeft: 8,
                      fontStyle: "italic",
                      color: "grey",
                      fontSize: 11
                    },
                    children: [
                      "(",
                      start + 1,
                      " - ",
                      end + 1,
                      ")"
                    ]
                  }
                )
              ]
            }
          ),
          value: id
        };
      }
    );
  };
  const forwardPrimers = getPrimers({ forward: true });
  const reversePrimers = getPrimers({ forward: false });
  let fPrimerId = forwardPrimer ?? forwardPrimers[0]?.value;
  let rPrimerId = reversePrimer ?? reversePrimers[0]?.value;
  const selectedF = primersById[fPrimerId];
  const selectedR = primersById[rPrimerId];
  if (selectedF?.primerId && !reversePrimer) {
    const pairedR = reversePrimers.find((p) => primersById[p.value]?.primerId === selectedF.primerId);
    if (pairedR) rPrimerId = pairedR.value;
  } else if (selectedR?.primerId && !forwardPrimer) {
    const pairedF = forwardPrimers.find((p) => primersById[p.value]?.primerId === selectedR.primerId);
    if (pairedF) fPrimerId = pairedF.value;
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "createPcrTool",
      style: { padding: 10, overflowY: "auto", height },
      children: [
        /* @__PURE__ */ jsx(
          ReactSelectField,
          {
            noResultsText: "Create a new primer in the forward direction to use it in this tool",
            inlineLabel: true,
            tooltipError: true,
            options: forwardPrimers,
            defaultValue: fPrimerId,
            name: "forwardPrimer",
            label: "Forward Primer:"
          }
        ),
        /* @__PURE__ */ jsx(
          ReactSelectField,
          {
            noResultsText: "Create a new primer in the reverse direction to use it in this tool",
            inlineLabel: true,
            tooltipError: true,
            options: reversePrimers,
            defaultValue: rPrimerId,
            name: "reversePrimer",
            label: "Reverse Primer:"
          }
        ),
        /* @__PURE__ */ jsx("div", { style: { fontWeight: "600", fontSize: 13 }, children: "Output Product:" }),
        seqBetween ? /* @__PURE__ */ jsx(
          SimpleCircularOrLinearView,
          {
            noWarnings: true,
            withZoomLinearView: true,
            withZoomCircularView: true,
            withChoosePreviewType: true,
            withDownload: true,
            smallSlider: true,
            withCaretEnabled: true,
            width: width - 50,
            height: Math.max(height - 250, 400),
            sequenceData: seqBetween,
            primerClicked: (args) => {
              primerClicked({
                ...args,
                annotation: primersById[args.annotation.originalId]
              });
            }
          }
        ) : /* @__PURE__ */ jsx("div", { style: { marginTop: 5, fontStyle: "italic", color: "grey" }, children: "Please choose a forward and reverse primer to see the resulting PCR sequence" })
      ]
    }
  );
}
export default compose(
  withEditorInteractions,
  reduxForm({ form: "PCRTool" }),
  tgFormValues("forwardPrimer", "reversePrimer")
)(PCRTool);
